import { DateTime } from 'luxon';

import { AssetSource, Permission } from '@/enums';
import type { Item } from '@/models';
import type { AccessSchedule, AccessSettings } from '@/models/channel';
import { AccessLevelType } from '@/models/channel';
import type { ContentSharingBehaviors, OU } from '@/models/Ou';
import type { Permissions } from '@/models/Permissions';
import type { AssetForm } from '@/store/forms';
import { SchemaType } from '@/store/forms/Asset-Editor/SchemaTypeOptions';
import clientUtils from './clientUtils';

export interface AccessLevelOption {
  label: string;
  value: AccessLevelType;
}

export const DefaultAccessLevelOptions: AccessLevelOption[] = [
  { label: 'Free', value: AccessLevelType.Free },
  { label: 'Private', value: AccessLevelType.Private },
];

export const AllAccessLevelOptions: AccessLevelOption[] = [
  { label: 'Free', value: AccessLevelType.Free },
  { label: 'Paywall', value: AccessLevelType.Paywall },
  { label: 'Private', value: AccessLevelType.Private },
];

/** Asset sources that may have per-asset Sharing Behavior when cross-OU sharing is enabled. */
export const CONTENT_SHARING_ELIGIBLE_SOURCES: AssetSource[] = [
  AssetSource.Ingest,
  AssetSource.SubClip,
  AssetSource.LiveStreamManager,
  AssetSource.LiveEncoder,
  AssetSource.AdVariant,
  AssetSource.ClipGroup,
];

/** Returns true when a schedule bound is empty (meaning beginning or end of time). */
const isOpenScheduleBound = (value: string | null | undefined): boolean => value == null || value === '';

/** Returns true when two access schedule time ranges overlap. */
const schedulesOverlap = (a: AccessSchedule, b: AccessSchedule): boolean => {
  const aFrom = isOpenScheduleBound(a.from) ? null : DateTime.fromISO(a.from);
  const aTo = isOpenScheduleBound(a.to) ? null : DateTime.fromISO(a.to);
  const bFrom = isOpenScheduleBound(b.from) ? null : DateTime.fromISO(b.from);
  const bTo = isOpenScheduleBound(b.to) ? null : DateTime.fromISO(b.to);

  if (aFrom && bTo && aFrom > bTo) {
    return false;
  }
  if (bFrom && aTo && bFrom > aTo) {
    return false;
  }
  return true;
};

const accessUtils = {
  /** @internal Updates access schedules for an expire date change; use applyExpireSet instead. */
  ensureExpireSet: (newAccessSchedules: AccessSchedule[], oldExpire: string, newExpire: string, force = false) => {
    const newSchedule: AccessSchedule = { from: newExpire, to: null, accessLevel: AccessLevelType.Private };
    if (oldExpire == null) {
      // we will need to create a new access schedule if newExpire is not null
      if (newExpire != null) {
        newAccessSchedules.push(newSchedule);
      }
    } else {
      const foundIndex = newAccessSchedules.findIndex((x) => x.from === oldExpire);
      if (foundIndex > -1) {
        if (newExpire == null) {
          // remove the thing from the list
          newAccessSchedules.splice(foundIndex, 1);
        } else {
          // console.log('updating newAccessSchedules at ', foundIndex);
          newAccessSchedules[foundIndex] = newSchedule;
        }
      } else if (force) {
        newAccessSchedules.push(newSchedule);
      }
    }
    return newAccessSchedules;
  },

  /** @internal Updates access schedules for an embargo date change; use applyEmbargoSet instead. */
  ensureEmbargoSet: (
    newAccessSchedules: AccessSchedule[],
    oldEmbargo: string,
    newEmbargo: string,
    force = false
  ): AccessSchedule[] => {
    const newSchedule: AccessSchedule = { from: null, to: newEmbargo, accessLevel: AccessLevelType.Private };
    if (oldEmbargo == null) {
      // we will need to create a new access schedule if newEmbargo is not null
      if (newEmbargo != null) {
        // console.log('inserting new access schedule at position 0', { newSchedule });
        newAccessSchedules.unshift(newSchedule);
      }
    } else {
      const foundIndex = newAccessSchedules.findIndex((x) => x.to === oldEmbargo);
      if (foundIndex > -1) {
        // console.log('updating newAccessSchedules at ', foundIndex);
        if (newEmbargo == null) {
          // remove the thing from the list
          newAccessSchedules.splice(foundIndex, 1);
        } else {
          newAccessSchedules[foundIndex] = newSchedule;
        }
      } else if (force) {
        // console.log('schedule not found, inserting new access schedule at position 0', { newSchedule });
        newAccessSchedules.unshift(newSchedule);
      }
    }
    return newAccessSchedules;
  },

  /** Returns an overlap error message if the proposed embargo date change is invalid, otherwise null. */
  validateEmbargoSetChange: (
    schedules: AccessSchedule[],
    oldEmbargo: string | null,
    newEmbargo: string | null,
    force = false
  ): string | null => {
    if (newEmbargo == null) {
      return null;
    }

    const newSchedule: AccessSchedule = { from: null, to: newEmbargo, accessLevel: AccessLevelType.Private };
    let replaceIndex: number | undefined;

    if (oldEmbargo != null) {
      const foundIndex = schedules.findIndex((x) => x.to === oldEmbargo);
      if (foundIndex > -1) {
        replaceIndex = foundIndex;
      } else if (!force) {
        return null;
      }
    }

    return accessUtils.validateScheduleOverlap(schedules, newSchedule, replaceIndex);
  },

  /** Returns an overlap error message if the proposed expire date change is invalid, otherwise null. */
  validateExpireSetChange: (
    schedules: AccessSchedule[],
    oldExpire: string | null,
    newExpire: string | null,
    force = false
  ): string | null => {
    if (newExpire == null) {
      return null;
    }

    const newSchedule: AccessSchedule = { from: newExpire, to: null, accessLevel: AccessLevelType.Private };
    let replaceIndex: number | undefined;

    if (oldExpire != null) {
      const foundIndex = schedules.findIndex((x) => x.from === oldExpire);
      if (foundIndex > -1) {
        replaceIndex = foundIndex;
      } else if (!force) {
        return null;
      }
    }

    return accessUtils.validateScheduleOverlap(schedules, newSchedule, replaceIndex);
  },

  /** Validates and applies an embargo date change, returning updated schedules or an error message. */
  applyEmbargoSet: (
    accessSchedules: AccessSchedule[],
    oldEmbargo: string | null,
    newEmbargo: string | null,
    force = false
  ): { accessSchedules: AccessSchedule[]; error: string | null } => {
    const error = accessUtils.validateEmbargoSetChange(accessSchedules, oldEmbargo, newEmbargo, force);
    if (error) {
      return { accessSchedules, error };
    }

    return {
      accessSchedules: accessUtils.ensureEmbargoSet([...accessSchedules], oldEmbargo, newEmbargo, force),
      error: null,
    };
  },

  /** Validates and applies an expire date change, returning updated schedules or an error message. */
  applyExpireSet: (
    accessSchedules: AccessSchedule[],
    oldExpire: string | null,
    newExpire: string | null,
    force = false
  ): { accessSchedules: AccessSchedule[]; error: string | null } => {
    const error = accessUtils.validateExpireSetChange(accessSchedules, oldExpire, newExpire, force);
    if (error) {
      return { accessSchedules, error };
    }

    return {
      accessSchedules: accessUtils.ensureExpireSet([...accessSchedules], oldExpire, newExpire, force),
      error: null,
    };
  },

  /** Returns an error if the candidate schedule overlaps others or breaks open-ended From/To rules. */
  validateScheduleOverlap: (schedules: AccessSchedule[], candidate: AccessSchedule, replaceIndex?: number): string | null => {
    const testSchedules =
      replaceIndex != null
        ? schedules.map((schedule, index) => (index === replaceIndex ? candidate : schedule))
        : [...schedules, candidate];

    let emptyFromCount = 0;
    let emptyToCount = 0;
    for (const schedule of testSchedules) {
      if (isOpenScheduleBound(schedule.from)) {
        emptyFromCount += 1;
      }
      if (isOpenScheduleBound(schedule.to)) {
        emptyToCount += 1;
      }
    }

    if (emptyFromCount > 1) {
      return 'Only one access schedule can have an empty From Date.';
    }
    if (emptyToCount > 1) {
      return 'Only one access schedule can have an empty To Date.';
    }

    for (let i = 0; i < testSchedules.length; i += 1) {
      for (let j = i + 1; j < testSchedules.length; j += 1) {
        if (schedulesOverlap(testSchedules[i], testSchedules[j])) {
          return 'Access schedules cannot overlap.';
        }
      }
    }

    return null;
  },

  /** Returns the embargo date from the Private schedule with an empty From date, if present. */
  resolveEmbargoDateFromSchedules: (schedules: AccessSchedule[]): string | null => {
    const schedule = schedules.find(
      (item) => isOpenScheduleBound(item.from) && !isOpenScheduleBound(item.to) && item.accessLevel === AccessLevelType.Private
    );
    return schedule?.to ?? null;
  },

  /** Returns the expire date from the Private schedule with an empty To date, if present. */
  resolveExpireDateFromSchedules: (schedules: AccessSchedule[]): string | null => {
    const schedule = schedules.find(
      (item) => !isOpenScheduleBound(item.from) && isOpenScheduleBound(item.to) && item.accessLevel === AccessLevelType.Private
    );
    return schedule?.from ?? null;
  },

  /** Writes embargo and expire form fields from the current access schedules after schedule edits. */
  syncEmbargoExpireFromSchedules: (
    schedules: AccessSchedule[],
    setValue: (name: string, value: unknown) => void,
    embargoDateField?: string,
    expireDateField?: string
  ) => {
    if (embargoDateField) {
      setValue(embargoDateField, accessUtils.resolveEmbargoDateFromSchedules(schedules));
    }
    if (expireDateField) {
      setValue(expireDateField, accessUtils.resolveExpireDateFromSchedules(schedules));
    }
  },

  getAccessLevelOptions: (ouData: OU) => {
    if (ouData?.metaData?.enablePaywall === true) {
      return AllAccessLevelOptions;
    }
    return DefaultAccessLevelOptions;
  },

  hasEditSharedAssetAccess: (permissions: Permissions | null | undefined): boolean => {
    return !!permissions?.has(Permission.EditSharedAssetAccess);
  },

  resolveContentSharingBehavior: (
    ouData: OU | null | undefined,
    contentSharingBehavior?: ContentSharingBehaviors | null
  ): ContentSharingBehaviors => {
    return contentSharingBehavior ?? ouData?.metaData?.contentSharing?.behavior ?? 'DoNotShare';
  },

  isContentSharingEligibleSource: (source: string | null | undefined): boolean => {
    // console.log('isContentSharingEligibleSource', { source });
    if (source == null) {
      return false;
    }
    return CONTENT_SHARING_ELIGIBLE_SOURCES.includes(source as AssetSource);
  },

  /**
   * Whether this asset type is eligible for the Sharing Behavior control (Video + eligible source).
   * Does not consider enableCrossOuSharing — use canEditContentSharing for that.
   */
  isContentSharingVisible: (
    ouData: OU | null | undefined,
    schemaType: SchemaType | string | null | undefined,
    source: string | null | undefined
  ): boolean => {
    return schemaType === SchemaType.Video && accessUtils.isContentSharingEligibleSource(source);
  },

  /**
   * Whether sharing behavior may be changed.
   * Requires EditSharedAssetAccess. Same-OU assets also need enableCrossOuSharing.
   * Partner/child assets viewed from another OU may be edited without the viewing OU flag.
   */
  canEditContentSharing: (permissions: Permissions | null | undefined, originalAssetData?: Item | null): boolean => {
    // console.log('canEditContentSharing', { permissions, ouData, schemaType, source, assetOuId, viewingOuId });
    if (accessUtils.hasEditSharedAssetAccess(permissions)) {
      return true;
    }
    const originalSharingBehavior = originalAssetData?.data?.contentSharing?.behavior;
    if (
      !accessUtils.hasEditSharedAssetAccess(permissions) &&
      (originalSharingBehavior == null || originalSharingBehavior === 'DoNotShare')
    ) {
      return true;
    }
    return false;
  },

  /** False when any selected asset cannot have sharing behavior bulk-edited. */
  isContentSharingBulkSelectionEligible: (
    permissions: Permissions | null | undefined,
    ouData: OU | null | undefined,
    assets: Item[]
  ): boolean => {
    if (
      !accessUtils.hasEditSharedAssetAccess(permissions) ||
      ouData?.metaData?.enableCrossOuSharing !== true ||
      assets.length === 0
    ) {
      return false;
    }
    return assets.every((asset) => accessUtils.isContentSharingVisible(ouData, asset.schemaType, asset.source));
  },

  getAccessScheduleCount: (accessSettings?: AccessSettings | null): number =>
    accessSettings?.temporal?.accessSchedules?.length ?? 0,

  /** True when a user without EditSharedAssetAccess moves from Do Not Share to Share With Parent Partner and schedules exist. */
  shouldConfirmShareWithParentPortalScheduleClear: (
    permissions: Permissions | null | undefined,
    oldBehavior: ContentSharingBehaviors | string | null | undefined,
    newBehavior: ContentSharingBehaviors | string | null | undefined,
    accessSettings?: AccessSettings | null
  ): boolean => {
    // console.log('shouldConfirmShareWithParentPortalScheduleClear', { permissions, oldBehavior, newBehavior, accessSettings });
    return (
      !accessUtils.hasEditSharedAssetAccess(permissions) &&
      (clientUtils.isNullOrWhitespace(oldBehavior) || (oldBehavior ?? 'DoNotShare') === 'DoNotShare') &&
      newBehavior === 'ShareWithParentPortal' &&
      accessUtils.getAccessScheduleCount(accessSettings) > 0
    );
  },

  SHARE_WITH_PARENT_PORTAL_SCHEDULE_CLEAR_CONFIRM:
    'Changing sharing to Share With Parent Partner will completely clear all access schedules. Do you want to continue?',

  /** Clears access schedules and synced embargo/expire fields after sharing moves to Share With Parent Partner. */
  clearAccessSchedulesInForm: (setValue: (name: string, value: unknown) => void, accessSettingsPath = 'accessSettings') => {
    setValue(`${accessSettingsPath}.temporal.accessSchedules`, []);
    accessUtils.syncEmbargoExpireFromSchedules([], setValue, 'embargoDate', 'expireDate');
  },

  /** Locks the Access tab and Details embargo/expire for users without EditSharedAssetAccess when sharing with parent partner. */
  lockAccessControls: (
    permissions: Permissions | null | undefined,
    ouData: OU | null | undefined,
    contentSharingBehavior?: ContentSharingBehaviors | null,
    schemaType?: string | null,
    source?: string | null
  ): boolean => {
    // console.log('lockAccessControls', { permissions, ouData, contentSharingBehavior, schemaType, source });
    if (accessUtils.hasEditSharedAssetAccess(permissions)) {
      return false;
    }
    if (contentSharingBehavior == null || contentSharingBehavior === 'DoNotShare') {
      return false;
    }
    return true;
    // if (clientUtils.isNullOrWhitespace(source) || accessUtils.isContentSharingVisible(ouData, schemaType, source)) {
    //   return false;
    // }
    // const effectiveBehavior = accessUtils.resolveContentSharingBehavior(ouData, contentSharingBehavior);
    // return effectiveBehavior === 'ShareWithParentPortal';
  },

  /** True when effective access is Private. */
  isEffectiveAccessPrivate: (item: Item | null | undefined): boolean => {
    return item?.data?.accessSettings?.effectiveAccessLevel === AccessLevelType.Private;
  },

  /**
   * True when a list thumbnail should show the cross-OU share indicator.
   * Based solely on the asset's contentSharing.behavior (not the OU enableCrossOuSharing flag).
   */
  shouldShowContentSharingIcon: (item: Item | null | undefined): boolean => {
    if (!item) {
      return false;
    }
    return item.data?.contentSharing?.behavior === 'ShareWithParentPortal';
  },
};

export { accessUtils };
